/**
 * カスタムロガーサービス
 * コンソール出力を一括で制御するためのモジュール
 */

// デバッグモードフラグ（ハードコーディング）
// trueに設定するとデバッグログが表示され、falseにすると制限されます
export const DEBUG_MODE = false; // ここを true/false で切り替え

// デフォルトのログレベル設定
const LOG_LEVELS = {
  DEBUG: 0,  // デバッグ情報（最も詳細）
  INFO: 1,   // 情報ログ
  WARN: 2,   // 警告ログ
  ERROR: 3,  // エラーログ
  NONE: 4    // ログ出力なし
};

// 現在のログレベル（デフォルトは全て表示）
let currentLogLevel = LOG_LEVELS.DEBUG;

// ログ出力を色付けするための設定
const LOG_STYLES = {
  DEBUG: 'color: #7f8c8d',      // グレー
  INFO: 'color: #2980b9',       // 青
  WARN: 'color: #f39c12',       // オレンジ
  ERROR: 'color: #c0392b',      // 赤
  APP: 'color: #8e44ad',        // パープル（アプリケーション固有のログ）
  PERF: 'color: #27ae60'        // グリーン（パフォーマンス関連ログ）
};

/**
 * ログレベルを設定する
 * @param {string} level - 設定するログレベル（'DEBUG', 'INFO', 'WARN', 'ERROR', 'NONE'）
 */
export function setLogLevel(level) {
  if (LOG_LEVELS[level] !== undefined) {
    currentLogLevel = LOG_LEVELS[level];
    
    // ログレベル変更を記録（この出力自体は常に表示）
    const originalConsoleLog = console.log;
    originalConsoleLog(`%cログレベルを [${level}] に設定しました`, `color: #16a085; font-weight: bold;`);
    
    // ローカルストレージに保存して次回アクセス時も同じ設定を維持
    try {
      localStorage.setItem('bkms_log_level', level);
    } catch (e) {
      // ローカルストレージへのアクセス例外は無視
    }
  } else {
    console.error(`無効なログレベル: ${level}`);
  }
}

/**
 * 保存されたログレベルを読み込む
 */
export function loadLogLevel() {
  try {
    const savedLevel = localStorage.getItem('bkms_log_level');
    if (savedLevel && LOG_LEVELS[savedLevel] !== undefined) {
      setLogLevel(savedLevel);
    }
  } catch (e) {
    // ローカルストレージへのアクセス例外は無視
  }
}

/**
 * デバッグログを出力
 * @param {string} message - ログメッセージ
 * @param {...any} args - 追加の引数
 */
export function debug(message, ...args) {
  if (currentLogLevel <= LOG_LEVELS.DEBUG) {
    console.log(`%c[DEBUG] ${message}`, LOG_STYLES.DEBUG, ...args);
  }
}

/**
 * 情報ログを出力
 * @param {string} message - ログメッセージ
 * @param {...any} args - 追加の引数
 */
export function info(message, ...args) {
  if (currentLogLevel <= LOG_LEVELS.INFO) {
    console.log(`%c[INFO] ${message}`, LOG_STYLES.INFO, ...args);
  }
}

/**
 * 警告ログを出力
 * @param {string} message - ログメッセージ
 * @param {...any} args - 追加の引数
 */
export function warn(message, ...args) {
  if (currentLogLevel <= LOG_LEVELS.WARN) {
    console.warn(`%c[WARN] ${message}`, LOG_STYLES.WARN, ...args);
  }
}

/**
 * エラーログを出力
 * @param {string} message - ログメッセージ
 * @param {...any} args - 追加の引数
 */
export function error(message, ...args) {
  if (currentLogLevel <= LOG_LEVELS.ERROR) {
    console.error(`%c[ERROR] ${message}`, LOG_STYLES.ERROR, ...args);
  }
}

/**
 * アプリケーション固有のログを出力
 * @param {string} component - コンポーネント名
 * @param {string} message - ログメッセージ
 * @param {...any} args - 追加の引数
 */
export function app(component, message, ...args) {
  if (currentLogLevel <= LOG_LEVELS.INFO) {
    console.log(`%c[${component}] ${message}`, LOG_STYLES.APP, ...args);
  }
}

/**
 * パフォーマンス計測用のログを出力
 * @param {string} operation - 操作名
 * @param {number} timeMs - 処理時間（ミリ秒）
 */
export function perf(operation, timeMs) {
  if (currentLogLevel <= LOG_LEVELS.DEBUG) {
    console.log(`%c[PERF] ${operation}: ${timeMs}ms`, LOG_STYLES.PERF);
  }
}

/**
 * Console APIを上書きしてグローバルにログレベルを適用
 * 注意: この機能は慎重に使用すること（副作用あり）
 * @param {boolean} override - trueの場合、Consoleオブジェクトを上書き
 */
export function overrideConsole(override = false) {
  if (override) {
    const originalConsole = {
      log: console.log,
      info: console.info,
      warn: console.warn,
      error: console.error
    };
    
    // console.log の上書き
    console.log = function(message, ...args) {
      if (currentLogLevel <= LOG_LEVELS.DEBUG) {
        originalConsole.log.apply(console, [message, ...args]);
      }
    };
    
    // console.info の上書き
    console.info = function(message, ...args) {
      if (currentLogLevel <= LOG_LEVELS.INFO) {
        originalConsole.info.apply(console, [message, ...args]);
      }
    };
    
    // console.warn の上書き
    console.warn = function(message, ...args) {
      if (currentLogLevel <= LOG_LEVELS.WARN) {
        originalConsole.warn.apply(console, [message, ...args]);
      }
    };
    
    // console.error の上書き
    console.error = function(message, ...args) {
      if (currentLogLevel <= LOG_LEVELS.ERROR) {
        originalConsole.error.apply(console, [message, ...args]);
      }
    };
  }
}

/**
 * ログレベルのショートカット関数をエクスポート
 */
export const LogLevels = {
  DEBUG: () => setLogLevel('DEBUG'),
  INFO: () => setLogLevel('INFO'),
  WARN: () => setLogLevel('WARN'),
  ERROR: () => setLogLevel('ERROR'),
  NONE: () => setLogLevel('NONE')
};

// デフォルト設定のエクスポート
export default {
  setLogLevel,
  loadLogLevel,
  debug,
  info,
  warn,
  error,
  app,
  perf,
  overrideConsole,
  LogLevels,
  LEVELS: LOG_LEVELS
};